<template>
  <section v-bind="$attrs" class="rounded-xl bg-gray-100 pb-8 ps-8 pt-8">
    <div class="mb-8 flex items-center justify-between pe-12 ps-4">
      <div class="text-subject font-bold text-dark">
        {{ `${$t('car')} ${$t('results')}` }}
      </div>

      <JButton
        v-if="!isLoadingSearchAndFilter && !carSearchResultStore.isSearchEmpty"
        type="button"
        size="xs"
        color="secondary"
        icon="filter"
        variant="tonal"
        @click="!carSearchResultStore.isLoading && setFilterDialog(true)"
      />
    </div>

    <MultiCitySearchResultSelector
      v-if="
        searchPanelHistory.history.isMultiDestination &&
        !carSearchResultStore.isSearchEmpty
      "
      :modelValue="city"
      class="mb-8"
      :disabled="isLoadingSearchAndFilter || carFiltersIsLoading"
      includeHotelOptions
      @update:model-value="onSelectCity"
    />

    <div
      v-if="!isLoadingSearchAndFilter && carSearchResultStore.totalCarCount > 0"
      class="my-12 flex items-center justify-between pe-12 ps-4"
    >
      <span class="font-bold">{{ $t('search.chooseYourCar') }}</span>
      <span class="font-medium text-gray-600">
        {{
          $t('search.nResults', {
            count: carSearchResultStore.totalCarCount,
          })
        }}
      </span>
    </div>

    <!-- CAROUSEL -->
    <div ref="swiperElementRef" class="swiper">
      <div class="swiper-wrapper">
        <template
          v-if="
            carSearchResultStore.carsList.length || isLoadingSearchAndFilter
          "
        >
          <template v-if="isLoadingSearchAndFilter">
            <div v-for="i in 4" :key="i" class="swiper-slide">
              <CarCardSkeleton />
            </div>
          </template>

          <template v-else>
            <div
              v-for="(car, index) in carSearchResultStore.carsList"
              :key="index"
              class="swiper-slide"
            >
              <CarCard
                :key="car.uniqueId"
                :car="car"
                @show-detail="showCarDetail(car)"
                @select="onSelect(car.uniqueId)"
                @delete="onDeselect(car.uniqueId)"
              />
            </div>

            <template
              v-if="
                carSearchResultStore.carsList.length !==
                carSearchResultStore.totalCarCount
              "
            >
              <div
                v-for="i in MINIMUM_RESULT_SIZE"
                :key="i"
                class="swiper-slide"
              >
                <CarCardSkeleton class="h-full" />
              </div>
            </template>
          </template>
        </template>

        <!-- NO RESULT -->
        <template v-else>
          <NoSearchResult
            :image="{ src: '/car.svg', alt: 'Car' }"
            :description="$t('search.noResultFound', { title: $t('car') })"
            class="w-full"
          />
        </template>
      </div>
    </div>
  </section>

  <!-- FILTER -->
  <JBottomSheet v-model="filterDialogIsOpen" hasPopstate>
    <template #header>
      <JContainer class="px-16 pt-24 text-pane-header font-bold text-dark">
        {{ $t('car') }} {{ $t('words.filters') }}
      </JContainer>
    </template>

    <div class="p-24">
      <CarResultsFilter @submit="onSubmitForm" />
    </div>
  </JBottomSheet>

  <!-- DETAIL -->
  <JBottomSheet v-model="carDetailIsOpen" fullScreen hasPopstate>
    <template #header>
      <JContainer class="flex justify-center px-16">
        <TheMobileHeader
          showBack
          :title="$t('search.carDetails')"
          titleClass="font-bold text-base flex-[10] px-16"
          @back="setCarDetailOpen(false)"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" @retry="refetch" />

    <CarDetail
      v-else
      :isLoading="carDetailIsFetching"
      @select="onSelect"
      @delete="onDeselect"
    />
  </JBottomSheet>
</template>

<script setup lang="ts">
import Swiper, { type Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { JBottomSheet, JButton, JContainer } from '@journey/ui-kit'
import { useI18n } from 'vue-i18n'
import { useToggle } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import useCarSearchResultsStore from '@/modules/Search/store/carSearchResults'
import CarCard from '@/modules/Core/components/shared/CarCard/CarCard.vue'
import CarCardSkeleton from '@/modules/Core/components/shared/CarCard/CarCardSkeleton/CarCardSkeleton.vue'
import { useCarInfoQuery } from '@/modules/Search/requests/queries'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import type { Car } from '@/modules/Search/types/model/search'
import type { SearchPanelCarFilters } from '@/modules/Search/types'
import {
  FilteredCarListSortBy,
  SearchOrFilterMode,
} from '@/modules/Search/types'
import type { FilteredCarListPayload } from '@/modules/Search/types/api/payload/filteredCarList'
import { useFilteredCarListMutation } from '@/modules/Search/requests/mutations'
import useCarSearchFiltersStore from '@/modules/Search/store/carSearchFilters'
import {
  DEFAULT_CAR_PAGE_SIZE,
  MINIMUM_RESULT_SIZE,
} from '@/modules/Search/constants'
import useSearchStore from '@/modules/Search/store'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import { useItinerary } from '@/modules/Search/composables'
import useToastStore from '@/modules/Core/store/toast'
import useAuthStore from '@/modules/Auth/store'
import featureFlag from '@/config/featureFlag'

defineOptions({
  inheritAttrs: false,
})

const MultiCitySearchResultSelector = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/MultiCitySearchResultSelector/MultiCitySearchResultSelector.vue'
    )
)
const CarDetail = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/CarDetail/CarDetail.vue')
)
const NoSearchResult = defineAsyncComponent(
  () => import('@/modules/Search/components/NoSearchResult/NoSearchResult.vue')
)
const CarResultsFilter = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/CarResults/CarResultsFilter/CarResultsFilter.vue'
    )
)
const ServerError = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/ServerError/ServerError.vue')
)
const TheMobileHeader = defineAsyncComponent(
  () =>
    import('@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue')
)

const swiperRef = ref<SwiperType | null>(null)
const swiperElementRef = ref<HTMLElement | null>(null)

onMounted(() => {
  swiperRef.value = new Swiper(swiperElementRef.value!, {
    slidesPerView: 1.3,
    spaceBetween: 10,
    on: {
      slideChange: onSlideChange,
    },
  })
})

const itinerarySummaryStore = useItinerarySummary()
const carSearchResultStore = useCarSearchResultsStore()
const carSearchFiltersStore = useCarSearchFiltersStore()
const searchPanelHistory = useSearchPanelHistoryStore()
const { submitItinerary } = useItinerary()
const authStore = useAuthStore()

const [filterDialogIsOpen, setFilterDialog] = useToggle(false)
const [carDetailIsOpen, setCarDetailOpen] = useToggle(false)

const provider = ref('')
const pickUpDate = ref('')
const dropOffDate = ref('')
const uniqueId = ref('')
const carCode = ref('')
const supplierCode = ref('')
const searchTerm = ref('')
const searchId = computed(() => searchPanelHistory.history.searchId ?? '')
const searchOption = ref(0)
const customerCode = ref(featureFlag.customerCode)
const city = ref()

const {
  data: carDetail,
  isFetching: carDetailIsFetching,
  isError,
  refetch,
} = useCarInfoQuery(searchId, searchOption, customerCode, {
  /* FIXME: `refetch` function doesn't work as expected. It returns
   *  `undefined` when called. For now, we enable the query by passing a
   *  `Ref<Boolean>` which behaves correctly.
   */
  enabled: carDetailIsOpen,
})

function showCarDetail(payload: Car) {
  /* TODO: The provider should be dynamic and filled with the proper name of
   *  the provider returned by the API. For now, the functionality is not
   *  implemented in the API side, so we hard-code the value.
   */
  provider.value = 'FlexibleAutos'
  pickUpDate.value = payload.pickupDateTime
  dropOffDate.value = payload.dropoffDateTime
  carCode.value = payload.code
  uniqueId.value = payload.uniqueId
  supplierCode.value = payload.supplierCode
  searchTerm.value = payload.iataCode
  searchOption.value = payload.searchOption

  setCarDetailOpen(true)
}

watch(carDetail, (value) => {
  if (!value) {
    return
  }

  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  carSearchResultStore.setSelectedCarInfo(value.data)
})

const toastStore = useToastStore()
const { t } = useI18n()

async function onSelect(uniqueId: string) {
  const car = carSearchResultStore.findCarByUniqueId(uniqueId)

  if (!car) {
    return
  }

  itinerarySummaryStore.select(car)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  toastStore.addToast({
    title: t('search.itinerarySummary.addMessages.carAdded'),
    color: 'success',
  })

  if (!carDetailIsOpen.value) {
    return
  }

  setCarDetailOpen(false)
}

async function onDeselect(uniqueId: string) {
  itinerarySummaryStore.remove(uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)
}

const apiPage = ref(1)
const size = ref(DEFAULT_CAR_PAGE_SIZE)
const query = ref('')

const { isLoading: carFiltersIsLoading, mutateAsync } =
  useFilteredCarListMutation({
    meta: {
      noSuccessToast: true,
    },
  })

const apiPayload = reactive<FilteredCarListPayload>({
  searchId: '',
  sortBy: carSearchFiltersStore.filters.sortBy,
  carType: carSearchFiltersStore.filters.sortCarType!,
  capacity: carSearchFiltersStore.filters.capacity,
  query: query.value,
  page: apiPage.value,
  size: size.value,
  residency: authStore.userResidency,
})

const searchStore = useSearchStore()
const [isLoadingFilter, toggleIsFilterLoading] = useToggle()

async function onSubmitFilters() {
  searchStore.setTriggeredBy(SearchOrFilterMode.Filter)

  apiPayload.searchId = searchId.value

  const { data } = await mutateAsync(apiPayload, {
    onError: () => {
      if (apiPayload.page === 1) {
        carSearchResultStore.setTotalCarPages(0)
        carSearchResultStore.setTotalCarCount(0)
      }
      toggleIsFilterLoading(false)
    },
  })

  if (!data) {
    return
  }

  const { items, page } = data.data

  carSearchResultStore.appendCars(items)

  if (page.items !== carSearchResultStore.totalCarCount) {
    carSearchResultStore.setTotalCarCount(page.items ?? 1)
  }
}

async function onSelectCity(value?: string) {
  toggleIsFilterLoading()
  city.value = value

  if (value) {
    apiPayload.destinations = [value.toLocaleLowerCase()]
  } else {
    delete apiPayload.destinations
  }

  apiPage.value = 1
  apiPayload.page = 1

  carSearchResultStore.resetCars()
  swiperRef.value?.slideTo(0)

  await onSubmitFilters()

  toggleIsFilterLoading()
}

async function onSubmitForm(filters: SearchPanelCarFilters) {
  toggleIsFilterLoading()

  apiPage.value = 1
  Object.assign(apiPayload, {
    sortBy: filters.sortBy,
    carType: filters.sortCarType!,
    capacity: filters.capacity,
    ...(filters.sortBy &&
      filters.sortBy === FilteredCarListSortBy.CarType && {
        sortCarType: filters.sortCarType,
      }),
    companies: filters.companies,
    page: 1,
    ...(city.value && {
      destinations: [city.value.toLocaleLowerCase()],
    }),
  } as SearchPanelCarFilters)

  if (filters.sortBy !== FilteredCarListSortBy.CarType) {
    delete apiPayload.sortCarType
  }

  setFilterDialog(false)

  carSearchResultStore.resetCars()
  swiperRef.value?.slideTo(0)

  await onSubmitFilters()

  toggleIsFilterLoading()
}

watch(isLoadingFilter, carSearchFiltersStore.setLoading)

const isLoadingSearchAndFilter = computed(
  () => carSearchResultStore.isLoading || isLoadingFilter.value
)

async function onSlideChange(event: any) {
  const activeIndex = event.activeIndex
  if (activeIndex === undefined) {
    return
  }

  if (
    carSearchResultStore.carsList.length === carSearchResultStore.totalCarCount
  ) {
    return
  }

  if ((activeIndex + 1) % (DEFAULT_CAR_PAGE_SIZE - 4) !== 0) {
    return
  }

  if (
    Math.ceil((activeIndex + 1) / DEFAULT_CAR_PAGE_SIZE) < apiPage.value &&
    apiPage.value !== 1
  ) {
    return
  }

  swiperRef.value?.update()

  await getFetchOrScrollCarStatus()

  swiperRef.value?.update()
}

async function getFetchOrScrollCarStatus() {
  if (isLoadingFilter.value) {
    return
  }

  apiPage.value++
  apiPayload.page = apiPage.value
  await onSubmitFilters()
}

watch(
  () => carSearchResultStore.isLoading,
  () => {
    apiPage.value = 1
  }
)

onBeforeUnmount(() => {
  swiperRef.value?.destroy()
})

defineExpose({
  onSelectCity,
})
</script>

<style lang="postcss" scoped>
.swiper {
  @apply z-0;
}
</style>
